import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Copy, ArrowDownToLine, ArrowUpFromLine, Check, ExternalLink, ShieldAlert } from "lucide-react";
import { WalletConnectors } from "@/components/WalletConnectors";

export default function Wallet() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("deposit");
  const [info, setInfo] = useState(null);
  const [fees, setFees] = useState(null);

  // Deposit
  const [txHash, setTxHash] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [copied, setCopied] = useState(false);

  // Withdraw
  const [wdAmount, setWdAmount] = useState("100");
  const [wdAddress, setWdAddress] = useState("");
  const [wdBusy, setWdBusy] = useState(false);

  const refresh = async () => {
    const { data } = await api.get("/wallet");
    setData(data);
  };

  useEffect(() => {
    refresh();
    api.get("/wallet/deposit/info").then((r) => setInfo(r.data)).catch((err) => console.warn("deposit/info failed:", err?.message));
    api.get("/wallet/fees").then((r) => setFees(r.data)).catch((err) => console.warn("fees failed:", err?.message));
  }, []);

  const verify = async () => {
    if (!/^0x[0-9a-fA-F]{64}$/.test(txHash.trim())) {
      toast.error("Enter a valid 0x… transaction hash (66 chars)");
      return;
    }
    setVerifying(true);
    try {
      const { data } = await api.post("/wallet/deposit/verify", { tx_hash: txHash.trim() });
      toast.success(`Credited ${data.amount_usdt} USDT`);
      setTxHash("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  const withdraw = async () => {
    setWdBusy(true);
    try {
      const { data } = await api.post("/wallet/withdraw", {
        amount_usdt: parseFloat(wdAmount),
        address: wdAddress.trim(),
      });
      toast.success(`Withdrawal queued (${data.payout_id}) — admin will process shortly`);
      setWdAddress("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Withdrawal failed");
    } finally {
      setWdBusy(false);
    }
  };

  const copyAddr = () => {
    if (!info?.house_address) return;
    navigator.clipboard.writeText(info.house_address);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (!data) return <div className="min-h-[calc(100vh-4rem)] grid place-items-center font-mono text-neon-green">LOADING WALLET…</div>;

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-10" data-testid="wallet-page">
      <div className="label-mono mb-3">// 02_wallet</div>
      <h1 className="font-display font-black text-3xl lg:text-5xl tracking-tighter mb-8">YOUR USDT VAULT.</h1>

      <WalletConnectors connected={data.connected_wallet} onChange={() => refresh()} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="panel p-6 lg:col-span-2 relative overflow-hidden">
          <div className="label-mono">Available Balance</div>
          <div className="font-mono font-black text-5xl lg:text-7xl text-neon-green glow-green mt-2" data-testid="wallet-balance-large">
            {data.balance_usdt.toFixed(2)} <span className="text-2xl text-white/40">USDT</span>
          </div>
          <div className="mt-4 label-mono">{data.transactions?.length || 0} TRANSACTIONS · ERC20 NETWORK · LIVE</div>
          <div className="absolute -right-12 -bottom-12 w-64 h-64 border border-neon-green/20 rotate-12 pointer-events-none" />
        </div>
        <div className="panel p-6 space-y-3">
          <div className="label-mono">Quick Actions</div>
          <button onClick={() => setTab("deposit")} className={`w-full text-left flex items-center gap-2 p-3 border transition-colors ${tab === "deposit" ? "border-neon-green text-neon-green" : "border-neon-line hover:border-white/40"}`} data-testid="tab-deposit">
            <ArrowDownToLine className="w-4 h-4" /> <span className="font-display font-bold uppercase tracking-wider text-sm">Deposit</span>
          </button>
          <button onClick={() => setTab("withdraw")} className={`w-full text-left flex items-center gap-2 p-3 border transition-colors ${tab === "withdraw" ? "border-neon-green text-neon-green" : "border-neon-line hover:border-white/40"}`} data-testid="tab-withdraw">
            <ArrowUpFromLine className="w-4 h-4" /> <span className="font-display font-bold uppercase tracking-wider text-sm">Withdraw</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="panel p-6 lg:col-span-2">
          {tab === "deposit" ? (
            <div className="space-y-5" data-testid="deposit-flow">
              <div>
                <h3 className="font-display font-black text-xl tracking-tight">DEPOSIT USDT (ERC20)</h3>
                <p className="text-white/50 text-sm font-body mt-1">
                  Send USDT from any wallet to the platform address, then paste your transaction hash below to credit your account.
                  {info?.min_deposit_usdt && <span className="block mt-1 text-neon-cyan">Minimum deposit: <b>{info.min_deposit_usdt} USDT</b></span>}
                </p>
              </div>

              <div className="panel !border-neon-green/40 p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="label-mono text-neon-green">// step 1 · platform wallet</div>
                  <div className="label-mono">ERC20 · USDT</div>
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 font-mono text-xs break-all bg-black p-3 border border-neon-line text-neon-green" data-testid="house-address">
                    {info?.house_address || "—"}
                  </code>
                  <button onClick={copyAddr} className="btn-ghost !py-3" data-testid="copy-house-address-button" title="Copy address">
                    {copied ? <Check className="w-4 h-4 text-neon-green" /> : <Copy className="w-4 h-4" />}
                  </button>
                  {info?.house_address && (
                    <a href={`https://etherscan.io/address/${info.house_address}`} target="_blank" rel="noreferrer" className="btn-ghost !py-3" title="View on Etherscan" data-testid="house-etherscan-link">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
                <div className="mt-3 flex items-start gap-2 text-xs font-mono text-neon-red/80">
                  <ShieldAlert className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>Only send <b>USDT on Ethereum (ERC20)</b>. Sending any other token or network will be lost.</span>
                </div>
              </div>

              <div className="panel !border-neon-line p-4">
                <div className="label-mono mb-3">// step 2 · paste your transaction hash</div>
                <div className="flex gap-2">
                  <input
                    className="field flex-1"
                    placeholder="0x… (66 chars)"
                    value={txHash}
                    onChange={(e) => setTxHash(e.target.value)}
                    spellCheck={false}
                    data-testid="deposit-txhash-input"
                  />
                  <button onClick={verify} disabled={verifying || !txHash.trim()} className="btn-neon disabled:opacity-30" data-testid="verify-deposit-button">
                    {verifying ? "VERIFYING…" : "VERIFY →"}
                  </button>
                </div>
                <div className="mt-3 text-xs font-mono text-white/40 leading-relaxed">
                  We verify on-chain via Etherscan: the tx must be confirmed ({info?.min_confirmations ?? 3}+ blocks), transfer USDT to the platform address, and not have been claimed before.
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4" data-testid="withdraw-form">
              <h3 className="font-display font-black text-xl tracking-tight">WITHDRAW USDT (ERC20)</h3>
              <p className="text-white/50 text-sm font-body">Withdrawals are reviewed and sent manually from the platform wallet within 24h.</p>
              {fees && (
                <div className="panel !border-neon-cyan/40 p-3 text-xs font-mono space-y-1" data-testid="fee-schedule">
                  <div className="label-mono text-neon-cyan">// fee schedule</div>
                  <div className="flex justify-between"><span className="text-white/60">min · max</span><span>{fees.min_withdrawal_usdt} · {fees.max_withdrawal_usdt} USDT</span></div>
                  <div className="flex justify-between"><span className="text-white/60">fee</span><span>max({fees.withdrawal_fee_flat_usdt} USDT, {fees.withdrawal_fee_percent}%)</span></div>
                </div>
              )}
              <div>
                <label className="label-mono">Your ERC20 Address</label>
                <input className="field mt-2" value={wdAddress} onChange={(e) => setWdAddress(e.target.value)} placeholder="0x… (42 chars)" spellCheck={false} data-testid="withdraw-address-input" />
              </div>
              <div>
                <label className="label-mono">Amount (USDT) {fees ? `· min ${fees.min_withdrawal_usdt}` : ""}</label>
                <input className="field mt-2" type="number" min={fees?.min_withdrawal_usdt || 10} step="0.5" value={wdAmount} onChange={(e) => setWdAmount(e.target.value)} data-testid="withdraw-amount-input" />
              </div>
              {fees && (() => {
                const amt = parseFloat(wdAmount) || 0;
                const fee = amt > 0 ? Math.max(fees.withdrawal_fee_flat_usdt, amt * fees.withdrawal_fee_percent / 100) : 0;
                const net = Math.max(0, amt - fee);
                return (
                  <div className="grid grid-cols-3 gap-3 text-xs font-mono" data-testid="withdraw-summary">
                    <div className="panel p-3"><div className="text-white/40">debited</div><div className="text-white font-bold mt-1">{amt.toFixed(2)}</div></div>
                    <div className="panel p-3"><div className="text-white/40">fee</div><div className="text-neon-red font-bold mt-1">{fee.toFixed(2)}</div></div>
                    <div className="panel !border-neon-green/40 p-3"><div className="text-neon-green">you receive</div><div className="text-neon-green font-bold mt-1" data-testid="withdraw-net-amount">{net.toFixed(2)}</div></div>
                  </div>
                );
              })()}
              <button onClick={withdraw} disabled={wdBusy} className="btn-danger w-full" data-testid="withdraw-button">
                {wdBusy ? "QUEUEING…" : "Submit Withdrawal"}
              </button>
              <div className="text-xs font-mono text-white/40">Funds are reserved instantly. If the request is cancelled by an admin, your full balance is refunded. Transfers are final once broadcast.</div>
            </div>
          )}
        </div>

        <div className="panel p-6">
          <h3 className="font-display font-black uppercase text-sm tracking-widest mb-4">// History</h3>
          <div className="space-y-2 max-h-[500px] overflow-y-auto" data-testid="tx-history">
            {data.transactions.length === 0 && <div className="text-white/40 text-xs font-mono">no transactions yet</div>}
            {data.transactions.map((tx) => (
              <div key={tx.tx_id} className="flex items-center justify-between py-2 border-b border-neon-line/60 last:border-0">
                <div>
                  <div className="font-mono text-xs uppercase text-white/60">{tx.kind}</div>
                  <div className="text-[10px] font-mono text-white/30">{new Date(tx.created_at).toLocaleString()}</div>
                  {tx.meta?.tx_hash && (
                    <a href={`https://etherscan.io/tx/${tx.meta.tx_hash}`} target="_blank" rel="noreferrer" className="text-[10px] font-mono text-neon-cyan hover:underline">
                      {tx.meta.tx_hash.slice(0, 10)}…
                    </a>
                  )}
                </div>
                <div className={`font-mono text-sm font-bold ${tx.amount > 0 ? "text-neon-green" : "text-neon-red"}`}>
                  {tx.amount > 0 ? "+" : ""}{tx.amount.toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
